/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */

    matchingQuery(value) {
      const aliases = [
        [/\bshock absorbers?\b/gi, 'shock'],
        [/\bdampers?\b/gi, 'shock'],
        [/\bspeed controllers?\b/gi, 'esc'],
        [/\belectronic speed controls?\b/gi, 'esc'],
        [/\bremote control\b/gi, 'rc'],
        [/\bradio controlled\b/gi, 'rc'],
        [/\btyres\b/gi, 'tires'],
        [/\btyre\b/gi, 'tire']
      ];
      let result = value;
      for (const [pattern, replacement] of aliases) result = result.replace(pattern, replacement);
      const vocabulary = ['traxxas', 'tamiya', 'arrma', 'spektrum', 'scalextric', 'hobbywing', 'battery', 'batteries', 'charger'];
      const distance = (a, b) => {
        const rows = Array.from({length:a.length + 1}, (_, i) => [i]);
        for (let j = 0; j <= b.length; j += 1) rows[0][j] = j;
        for (let i = 1; i <= a.length; i += 1) {
          for (let j = 1; j <= b.length; j += 1) {
            rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
          }
        }
        return rows[a.length][b.length];
      };
      return result.replace(/\b[a-z]{5,}\b/gi, (word, offset) => {
        if (/[0-9_-]/.test(result[offset - 1] || '') || /[0-9_-]/.test(result[offset + word.length] || '')) return word;
        const lower = word.toLowerCase();
        if (vocabulary.some(term => term === lower || term.startsWith(lower))) return word;
        const candidates = vocabulary.filter(term => distance(lower, term) === 1);
        return candidates.length === 1 ? candidates[0] : word;
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      } else {
        this.input.value = this.matchingQuery(this.getQuery());
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */

    async getResults(searchTerm) {
      const queryKey = searchTerm.toLowerCase();
      const matchedTerm = this.matchingQuery(searchTerm);
      const requestId = (this.requestId || 0) + 1;
      this.requestId = requestId;
      this.setLiveRegionLoadingState();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }
      const searchTypes = [];
      if (theme.settings.pSearchShowProducts) searchTypes.push('product');
      if (theme.settings.pSearchShowCollections) searchTypes.push('collection');
      if (theme.settings.pSearchShowPages) searchTypes.push('page');
      if (theme.settings.pSearchShowArticles) searchTypes.push('article');
      const fetchResults = async (term) => {
        const params = new URLSearchParams({
          q: term,
          'resources[type]': searchTypes.join(','),
          'resources[limit]': theme.settings.pSearchLimit,
          'resources[limit_scope]': theme.settings.pSearchLimitScope,
          'resources[options][unavailable_products]': 'last',
          section_id: 'predictive-search'
        });
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`);
        if (!response.ok) throw new Error(response.status);
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        return tmpl.content.querySelector('#shopify-section-predictive-search');
      };
      try {
        const terms = matchedTerm.toLowerCase() === queryKey ? [searchTerm] : [searchTerm, matchedTerm];
        const responses = await Promise.allSettled(terms.map(fetchResults));
        const roots = responses.filter(response => response.status === 'fulfilled').map(response => response.value).filter(Boolean);
        if (!roots.length) throw new Error('Search unavailable');
        const root = roots.find(el => el.querySelector('[data-hbt-product]')) || roots[0];
        const list = root.querySelector('[data-hbt-product]')?.parentElement;
        if (list) {
          const products = new Map();
          roots.forEach(el => el.querySelectorAll('[data-hbt-product]').forEach(item => {
            if (!products.has(item.dataset.hbtProduct)) products.set(item.dataset.hbtProduct, item);
          }));
          const exact = item => {
            const skus = JSON.parse(item.dataset.hbtSkus || '[]');
            if (item.dataset.hbtTitle === queryKey || skus.some(sku => sku && sku.toLowerCase() === queryKey)) return 0;
            const title = item.dataset.hbtTitle || '';
            const strongMatch = [searchTerm, matchedTerm].some(term => term.toLowerCase().split(/\s+/).filter(Boolean).every(word => title.includes(word)));
            return strongMatch ? 1 : 2;
          };
          const ordered = Array.from(products.values()).sort((left, right) =>
            exact(left) - exact(right) || Number(left.dataset.hbtStockRank) - Number(right.dataset.hbtStockRank)
          ).slice(0, Number(theme.settings.pSearchLimit) || 10);
          list.querySelectorAll('[data-hbt-product]').forEach(item => item.remove());
          const submit = list.querySelector('.js-submit');
          ordered.forEach(item => list.insertBefore(item, submit));
        }
        if (terms.length > 1) {
          const note = document.createElement('div');
          note.className = 'hbt-search-matching';
          note.textContent = root.querySelector('[data-hbt-matching-label]')?.dataset.hbtMatchingLabel.replace('__query__', matchedTerm) || matchedTerm;
          root.querySelector('.predictive-search__results')?.prepend(note);
        }
        root.querySelectorAll('.predictive-search__item').forEach((item, index) => { item.id = `psearch-result-${index}`; });
        const markup = root.innerHTML.replace(/psearch/g, this.input.id);
        this.cachedResults[queryKey] = markup;
        if (requestId === this.requestId && this.getQuery().toLowerCase() === queryKey) this.renderResults(markup);
      } catch (error) {
        if (requestId === this.requestId) this.close();
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.statusEl.textContent = '';
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      this.requestId = (this.requestId || 0) + 1;
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
